// react import
import { useCallback, useEffect, useRef, useState } from "react";
// react router import
import { useNavigate } from "react-router-dom";
// redux imports
import { useDispatch, useSelector } from "react-redux";
// redux slices import
import {
  allOrganizations,
  loggedUser,
  setActiveOrganization,
  appConfigLoader,
  allRaffleLotteries,
  setNoaOrganization,
} from "../store/slices/gammaSlice";
import { gameOffersUpdate } from "../store/slices/gammaSlice";
import {
  uiConfigUpdate,
  uiLogosUpdate,
  uiProfilePictureUpdate,
  uiOrganizationLogo,
  resetLogos,
} from "../store/slices/localSlice";
// utils import
import { roles } from "../config/strings";
// api imports
import gamesService from "../api/games/GamesService";
import themeService from "../api/themes/ThemeService";
import { getAllConfigurations } from "../api/config/ConfigService";
import raffleService from "../api/raffles/RafflesService";
import { gammaServices } from "../api/gamma";
// material ui import
import CircularProgress from "@mui/material/CircularProgress";
// custom components import
import { ComponentError } from "../components/ErrorPages/ComponentsError";
// translate import
import { useTranslation } from "react-i18next";
import { useAuth } from "../components/Auth/AuthProvider";
import authService from "../services/auth.service";
import deliveryServices from "../api/delivery/DeliveryService";
import { getNoaId } from "../utils/session.utils";

const INIT = "init";
const SETTLED = "settled";

// =============================== | PRELOAD | =================================

export const Preload = () => {
  // Section: Hooks
  const { session, userId } = useAuth(); // using hook to get current session
  const gammaService = gammaServices;
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.gamma.logged_user);
  const activeOrganization = useSelector(
    (state) => state.gamma.activeOrganization,
  );
  const [organizationId, setOrganizationId] = useState(undefined);

  const { uiConfig } = useSelector((state) => state.local);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [backError, setBackError] = useState({
    error: false,
  });
  const [loadedConfigurations, setLoadedConfigurations] = useState(false);
  const [loadedLoggedUser, setLoadedLoggedUser] = useState(false);
  const [loadedCurrentTheme, setLoadedCurrentTheme] = useState(false);
  const [loadedCurrentLogo, setLoadedCurrentLogo] = useState(false);
  const [loadedGameOffers, setLoadedGameOffers] = useState(false);
  const [loadedActiveGameOffers, setLoadedActiveGameOffers] = useState(false);
  const [loadedOrganizations, setLoadedOrganizations] = useState(false);
  const [loadedRaffleLotteries, setLoadedRaffleLotteries] = useState(false);
  const [noaOrganizations, setNoaOrganizations] = useState(false);

  // Section: Reference, it helps to get the data just once
  const ref = useRef({
    conf: INIT,
    usr: INIT,
    theme: INIT,
    org: INIT,
    allOrg: INIT,
    roles: INIT,
    orgConf: INIT,
    legals: INIT,
    legal: INIT,
    agencyRelation: INIT,
    agency: INIT,
    gameOffer: INIT,
    gameActive: INIT,
    allOrganizations: INIT,
    raffleService: INIT,
    noaOrganizationService: INIT,
  });

  useEffect(() => {
    if (uiConfig) {
      dispatch(resetLogos());
      if (uiConfig.profilePicture) {
        dispatch(uiProfilePictureUpdate(uiConfig.profilePicture));
      }
      if (uiConfig.logos) {
        dispatch(uiLogosUpdate(uiConfig.logos));
      }
    }
  }, [dispatch, uiConfig]);

  const isErrorResponse = (res) => {
    return res?.status && !(res?.status >= 200 && res?.status < 300);
  };

  const handleLoadingError = (e, message) => {
    setBackError({
      error: true,
      code: message || e?.status || 500,
    });
  };

  const isValidMemberships = (memberships) => {
    return (
      Array.isArray(memberships) &&
      memberships.length > 0 &&
      memberships[0].organization &&
      memberships[0].organization.id &&
      memberships[0].organization.name &&
      Array.isArray(memberships[0].roles) &&
      memberships[0].roles.length > 0
    );
  };

  useEffect(() => {
    if (activeOrganization) {
      deliveryServices
        .getNoaOrganization(activeOrganization.id)
        .then((data) => {
          dispatch(setNoaOrganization(data));
          setOrganizationId(data.id);
          setNoaOrganizations(true);
        })
        .catch((error) => {
          console.error("Error at getting organization Id", error);
        });
    }
  }, [activeOrganization, dispatch, loadedOrganizations]);

  useEffect(() => {
    if (!loadedLoggedUser && ref.current.usr === INIT) {
      ref.current.usr = SETTLED;
      authService
        .getUser(userId)
        .then((res) => {
          if (isErrorResponse(res)) {
            handleLoadingError(res, t("loading.loggedUserError"));
          } else {
            const keycloakRoles = session.decodedToken.realm_access.roles.map(
              (rol, index) => {
                return {
                  id: index,
                  name: rol,
                  description: rol,
                };
              },
            );
            const responseMembership = Array.isArray(res?.memberships)
              ? res?.memberships[0]
              : undefined;
            responseMembership.roles = [{ ...keycloakRoles[0] }];
            const newLoggedUser = {
              ...res,
              memberships: [{ ...responseMembership, agencies: null }],
            };
            if (isValidMemberships(newLoggedUser?.memberships)) {
              const userOrganization =
                newLoggedUser.memberships[0].organization;
              dispatch(setActiveOrganization(userOrganization));
              dispatch(loggedUser(newLoggedUser));
              setLoadedLoggedUser(true);
            } else {
              navigate("/logout", { replace: true });
            }
          }
        })
        .catch((e) => {
          handleLoadingError(e, t("loading.loggedUserError"));
        });
    }
  }, [
    dispatch,
    loadedConfigurations,
    loadedLoggedUser,
    navigate,
    session.accesstoken,
    session.decodedToken.realm_access.roles,
    t,
    userId,
  ]);

  useEffect(() => {
    if (loadedLoggedUser && ref.current.conf === INIT) {
      ref.current.conf = SETTLED;

      getAllConfigurations(organizationId)
        .then((res) => {
          dispatch(appConfigLoader(res.content));
          setLoadedConfigurations(true);
        })
        .catch((e) => {
          handleLoadingError(e, t("loading.configurationError"));
        });
    }
  }, [
    organizationId,
    currentUser,
    dispatch,
    loadedConfigurations,
    loadedLoggedUser,
    t,
  ]);

  useEffect(() => {
    if (loadedLoggedUser && !loadedCurrentTheme && ref.current.theme === INIT) {
      ref.current.theme = SETTLED;
      themeService
        .currentTheme(organizationId)
        .then((currentSelectedTheme) => {
          const themeColors = {
            id: currentSelectedTheme.id,
            title: currentSelectedTheme.displayName,
            primary: {
              main: currentSelectedTheme.primary.normal,
              light: currentSelectedTheme.primary.light,
              dark: currentSelectedTheme.primary.dark,
            },
            secondary: {
              main: currentSelectedTheme.secondary.normal,
              light: currentSelectedTheme.secondary.light,
              dark: currentSelectedTheme.secondary.dark,
            },
            custom:
              (uiConfig?.custom
                ? uiConfig.custom
                : currentSelectedTheme.custom) || {},
          };
          dispatch(uiConfigUpdate(themeColors));
          setLoadedCurrentTheme(true);
        })
        .catch((e) => {
          handleLoadingError(e, t("loading.themeError"));
        });
    }
  }, [
    organizationId,
    currentUser,
    dispatch,
    loadedCurrentTheme,
    loadedLoggedUser,
    t,
    uiConfig,
  ]);

  useEffect(() => {
    if (loadedLoggedUser && !loadedCurrentLogo && ref.current.org === INIT) {
      ref.current.org = SETTLED;

      themeService
        .currentLogo(organizationId)
        .then((currLogo) => {
          dispatch(uiOrganizationLogo(currLogo));
          setLoadedCurrentLogo(true);
        })
        .catch((e) => {
          handleLoadingError(e, t("loading.logoError"));
        });
    }
  }, [organizationId, dispatch, loadedCurrentLogo, loadedLoggedUser, t]);

  useEffect(() => {
    if (
      organizationId &&
      loadedLoggedUser &&
      !loadedGameOffers &&
      ref.current.gameOffer === INIT
    ) {
      ref.current.gameOffer = SETTLED;

      gamesService
        .getGameOffers(1)
        .then((res) => {
          dispatch(gameOffersUpdate(res));
          setLoadedGameOffers(true);
        })
        .catch((e) => {
          handleLoadingError(e, t("loading.gamesError"));
        });
    }
  }, [organizationId, dispatch, loadedGameOffers, loadedLoggedUser, t]);

  useEffect(() => {
    if (
      loadedLoggedUser &&
      !loadedOrganizations &&
      ref.current.allOrganizations === INIT
    ) {
      ref.current.allOrganizations = SETTLED;
      gammaService
        .getAllOrganizations()
        .then((res) => {
          dispatch(allOrganizations(res.content));
          setLoadedOrganizations(true);
        })
        .catch((e) => {
          handleLoadingError(e, t("loading.organizationsError"));
        });
    }
  }, [
    dispatch,
    gammaService,
    loadedConfigurations,
    loadedLoggedUser,
    loadedOrganizations,
    t,
  ]);

  useEffect(() => {
    if (
      loadedLoggedUser &&
      !loadedRaffleLotteries &&
      ref.current.raffleService === INIT
    ) {
      ref.current.raffleService = SETTLED;
      raffleService
        .getRaffleLoteries(activeOrganization.id)
        .then((res) => {
          dispatch(allRaffleLotteries(res.content));
          setLoadedRaffleLotteries(true);
        })
        .catch((e) => {
          handleLoadingError(e, t("loading.raffleLotteriesError"));
        });
    }
  }, [
    dispatch,
    loadedConfigurations,
    loadedLoggedUser,
    loadedRaffleLotteries,
    t,
  ]);

  useEffect(() => {
    if (
      organizationId &&
      loadedLoggedUser &&
      !loadedActiveGameOffers &&
      ref.current.gameActive === INIT
    ) {
      ref.current.gameActive = SETTLED;
      gamesService
        .getAllActivesGameOffers(dispatch)
        .then((res) => {
          setLoadedActiveGameOffers(true);
        })
        .catch((e) => {
          handleLoadingError(e, t("loading.activeGamesError"));
        });
    }
  }, [
    organizationId,
    dispatch,
    loadedActiveGameOffers,
    loadedConfigurations,
    loadedLoggedUser,
    t,
  ]);

  const allConfigurationsLoaded = useCallback(() => {
    return (
      loadedConfigurations &&
      loadedLoggedUser &&
      loadedCurrentTheme &&
      loadedCurrentLogo &&
      loadedGameOffers &&
      loadedActiveGameOffers &&
      loadedOrganizations &&
      loadedRaffleLotteries &&
      noaOrganizations
    );
  }, [
    loadedActiveGameOffers,
    loadedConfigurations,
    loadedCurrentLogo,
    loadedCurrentTheme,
    loadedGameOffers,
    loadedLoggedUser,
    loadedOrganizations,
    loadedRaffleLotteries,
    noaOrganizations,
  ]);

  useEffect(() => {
    if (allConfigurationsLoaded()) {
      const currentRole =
        Array.isArray(currentUser?.memberships) &&
        currentUser?.memberships[0]?.roles[0]?.name;

      if (currentRole === roles.administradorLoteria) {
        /* ES ADMINISTADOR DE LOTERIA */
        navigate(`../admin/users`);
      } else if (
        (Array.isArray(currentUser?.memberships) &&
          currentUser?.memberships[0]?.roles[0]?.name === roles.autorizador) ||
        currentUser?.memberships[0]?.roles[0]?.name === roles.operador
      ) {
        /* ES AGENCIERO */
        navigate(`../main/productos`);
      } else {
        /* TODO: No tiene Rol hay que poner pantalla de No autorizado con logout automatico  */
        navigate(`/logout`);
      }
    }
  }, [allConfigurationsLoaded, currentUser?.memberships, navigate]);

  const styles = {
    width: "100%",
    height: "100vh",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  };

  const stylesH4 = {
    fontSize: "1.8rem",
    fontWeight: "300",
    fontFamily: "Roboto, sans-serif",
  };

  return backError?.error ? (
    <ComponentError code={backError?.code} />
  ) : (
    <div style={styles}>
      <CircularProgress size={"2rem"} />
      <h4 style={stylesH4}>{t("validateData")}</h4>
    </div>
  );
};
